import useFormValidation from "../../hooks/useFormValidation";
import Button from "../../components/ui/Button";
import Modal from "../../components/ui/Modal";
import React, { useState, useEffect } from "react";
import "./EditVendorModal.css";
import { Vendor } from "../../types/vendor";

interface EditVendorModalProps {
  vendor: Vendor;
  onClose: () => void;
  onSave: (updatedVendor: Partial<Vendor>) => void; // We pass only updated fields
  isSaving: boolean;
}

const EditVendorModal: React.FC<EditVendorModalProps> = ({
  vendor,
  onClose,
  onSave,
  isSaving,
}) => {
  const { errors, validate, clearError } = useFormValidation();
  const [formData, setFormData] = useState<Partial<Vendor>>({
    name: "",
    email: "",
    phone: "",
    address: "",
    description: "",
  });

  // Pre-fill the form when the modal opens
  useEffect(() => {
    if (vendor) {
      setFormData({
        name: vendor.name,
        email: vendor.email,
        phone: vendor.phone,
        address: vendor.address,
        description: vendor.description,
        isActive: vendor.isActive,
      });
    }
  }, [vendor]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    clearError(name);
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleStatusChange = (isActive: boolean) => {
    setFormData((prev) => ({ ...prev, isActive }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate(e.currentTarget as HTMLFormElement)) return;
    onSave({ ...formData, name: formData.name?.trim(), email: formData.email?.trim().toLowerCase(), phone: formData.phone?.trim(), address: formData.address?.trim() });
  };

  return (
    <Modal title={<>Edit Vendor</>} onClose={onClose} size="medium">
<div className="modal-content legacy-modal-content">

        <form noValidate onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="name">Vendor Name</label>
            <input
              type="text"
              id="name"
              name="name"
              aria-invalid={!!errors.name} aria-describedby={errors.name ? "vendor-name-error" : undefined}
              disabled={isSaving}
              value={formData.name}
              onChange={handleChange}
              required
            />
            {errors.name && <p id="vendor-name-error" className="navo-field-error" role="alert">{errors.name}</p>}
          </div>
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email" autoComplete="email" placeholder="vendor@example.com" maxLength={254}
              id="email"
              name="email"
              aria-invalid={!!errors.email} aria-describedby={errors.email ? "vendor-email-error" : undefined}
              disabled={isSaving}
              value={formData.email}
              onChange={handleChange}
              required
            />
            {errors.email && <p id="vendor-email-error" className="navo-field-error" role="alert">{errors.email}</p>}
          </div>
          <div className="form-group">
            <label htmlFor="phone">Phone Number</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              aria-invalid={!!errors.phone} aria-describedby={errors.phone ? "vendor-phone-error" : undefined}
              disabled={isSaving}
              value={formData.phone}
              onChange={handleChange}
              required
            />
            {errors.phone && <p id="vendor-phone-error" className="navo-field-error" role="alert">{errors.phone}</p>}
          </div>
          <div className="form-group">
            <label htmlFor="address">Address</label>
            <textarea
              id="address"
              name="address"
              aria-invalid={!!errors.address} aria-describedby={errors.address ? "vendor-address-error" : undefined}
              disabled={isSaving}
              value={formData.address}
              onChange={handleChange}
              rows={3}
            ></textarea>
            {errors.address && <p id="vendor-address-error" className="navo-field-error" role="alert">{errors.address}</p>}
          </div>
          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              aria-invalid={!!errors.description} aria-describedby={errors.description ? "vendor-description-error" : undefined}
              disabled={isSaving}
              value={formData.description}
              onChange={handleChange}
              rows={3}
            ></textarea>
            {errors.description && <p id="vendor-description-error" className="navo-field-error" role="alert">{errors.description}</p>}
          </div>
          <div className="form-group">
            <label>Status</label>
            <div className="status-toggle">
              <Button
                type="button"
                className={formData.isActive ? "active" : ""}
                onClick={() => handleStatusChange(true)}
              >
                Active
              </Button>
              <Button
                type="button"
                className={!formData.isActive ? "active" : ""}
                onClick={() => handleStatusChange(false)}
              >
                Inactive
              </Button>
            </div>
          </div>

          <div className="modal-actions">
            <Button
              type="button"
              className="btn-cancel"
              onClick={onClose}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button variant="primary" type="submit" className="btn-save" disabled={isSaving}>
              {isSaving ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </form>
      </div>
</Modal>
  );
};

export default EditVendorModal;
